import { ApiUrlInput } from '@neo/blocks/api-url-input'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Label } from '@neo/primitives/label'
import { Tooltip } from '@neo/primitives/tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useParams } from 'router-v6'
import { FlowSelect } from 'v2/shared/components/flow-select'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type { z } from 'zod'
import {
  AuthType,
  type eventCallbackBase,
  eventCallbacksSchema,
  getEventCallbackDefaults,
} from '../../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../../config/base.config'
import { useAllNodeVars } from '../../hooks/use-all-node-vars'
import { type FlowData, StartNodeTypes } from '../../types'
import { generateEventCallbackSchema } from '../../utils'
import { AuthConfig } from './auth-config'
import { SchemaDialog } from './schema-dialog'

export type EventCallbackKey = keyof z.infer<typeof eventCallbacksSchema>
// Generate field paths dynamically from schema - now supports HANGUP, RECORDING, and ERROR
export type EventCallbackFieldPath = `event_callbacks.${EventCallbackKey}`
type EventCallbacks = z.infer<typeof eventCallbacksSchema>
type HangupCallback = EventCallbacks['HANGUP']

// Helper function to get current value based on fieldPath
const getCurrentValue = (fieldPath: EventCallbackFieldPath, values: AgentSettingsFormValues) => {
  const key = fieldPath?.split('.')?.[1] as keyof EventCallbacks
  return values.event_callbacks?.[key]
}

// Helper function to update field values with proper typing and schema defaults
const updateFieldValue = (
  form: GenericFormType<AgentSettingsFormValues>,
  fieldPath: EventCallbackFieldPath,
  updatedValue: Partial<EventCallbacks[keyof EventCallbacks]>,
) => {
  const key = fieldPath?.split('.')?.[1] as keyof EventCallbacks
  const defaults = getEventCallbackDefaults()[key]
  const mergedValue = { ...defaults, ...updatedValue }
  form.setFieldValue(fieldPath, mergedValue as EventCallbacks[keyof EventCallbacks])
}

interface EventCallbackFieldProps {
  label: string
  form: GenericFormType<AgentSettingsFormValues>
  fieldPath: EventCallbackFieldPath
}

// Create fields configuration outside component to prevent recreation
const createEventCallbackFields = (
  fieldPath: EventCallbackFieldPath,
  label: string,
  effectiveShowAuth: boolean,
  isOpen: boolean,
  onOpenChange: (open: boolean) => void,
  generateSchema: string,
  handleToggleAuth: () => void,
  form: GenericFormType<AgentSettingsFormValues>,
): GenericFieldConfig<AgentSettingsFormValues> => [
  {
    name: 'label-url',
    type: 'slot',
    component: <Label>{label}</Label>,
  },
  {
    name: `${fieldPath}.type`,
    type: 'sub-item',
    componentType: 'radioGroup',
    visible: fieldPath === 'event_callbacks.HANGUP',
    zodValidation:
      fieldPath === 'event_callbacks.HANGUP'
        ? eventCallbacksSchema.shape.HANGUP.shape.type
        : undefined,
    getDynamicProps: (values: AgentSettingsFormValues) => ({
      inputProps: {
        onChange: (curValue: 'url' | 'agent') => {
          const currentValue = getCurrentValue(fieldPath, values)
          updateFieldValue(form, fieldPath, {
            ...currentValue,
            type: curValue,
            url: '', // Reset URL when changing type
          })
          form.validateField(`${fieldPath}.url`, 'change')
        },
        options: [
          { label: 'URL', value: 'url' },
          { label: 'Trigger an agent', value: 'agent' },
        ],
      },
    }),
  },
  {
    name: `${fieldPath}.url`,
    type: 'custom',
    onChangeListenTo: ['event_callbacks.HANGUP.type'],
    afterOnChange: (values, _, formInstance) => {
      if (fieldPath === 'event_callbacks.RECORDING') {
        const recordingUrl = values.event_callbacks?.RECORDING?.url
        if (!recordingUrl?.trim()) {
          formInstance?.setFieldValue('event_callbacks.RECORDING.transcription', false)
          formInstance?.validateField('event_callbacks.RECORDING.transcription', 'change')
        }
      }
    },
    component: ({ form: fieldForm }) => {
      const currentValue = getCurrentValue(fieldPath, fieldForm.state.values)

      return (
        <div className="flex flex-row gap-2">
          {fieldPath === 'event_callbacks.HANGUP' &&
          (currentValue as HangupCallback)?.type === 'agent' ? (
            <FlowSelect
              phloType={StartNodeTypes.start_api_request}
              valueKey="url"
              data-testid="select-callback-agent"
              onChange={(url) => {
                if (currentValue) {
                  const updatedValue = { ...currentValue, url: url as string }
                  updateFieldValue(form, fieldPath, updatedValue)
                  fieldForm.validateField(`${fieldPath}.url`, 'change')
                }
              }}
              placeholder="Select an agent"
              value={currentValue?.url || ''}
            />
          ) : (
            <ApiUrlInput
              methods={['POST', 'GET']}
              defaultMethod={currentValue?.method}
              url={currentValue?.url || ''}
              onMethodChange={(method) => {
                if (currentValue) {
                  const updatedValue = {
                    ...currentValue,
                    method: method as z.infer<typeof eventCallbackBase>['method'],
                  }
                  updateFieldValue(form, fieldPath, updatedValue)
                }
              }}
              onUrlChange={(url) => {
                if (currentValue) {
                  const updatedValue = { ...currentValue, url }
                  updateFieldValue(form, fieldPath, updatedValue)
                  fieldForm.validateField(`${fieldPath}.url`, 'change')
                }
              }}
              id={`event-callback-url-${label.replace(/\s+/g, '-').toLowerCase()}`}
            />
          )}
          <Tooltip content="Add Auth">
            <Button
              type="button"
              variant="outline"
              className={cn(
                'whitespace-nowrap px-2 text-secondary hover:bg-hv1 hover:text-foreground',
                effectiveShowAuth &&
                  'border-brand bg-brand/10 text-brand-text hover:bg-brand/15 hover:text-brand-text',
              )}
              onClick={handleToggleAuth}
              aria-label={`Configure authentication for ${label}`}
              data-testid="toggle-auth-btn"
            >
              <KeyRound size={16} />
            </Button>
          </Tooltip>

          <SchemaDialog
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            fieldPath={fieldPath}
            generateSchema={generateSchema}
          />
        </div>
      )
    },
    zodValidation: (() => {
      const eventKey = fieldPath?.split('.')?.[1] as EventCallbackKey
      const eventSchema = eventCallbacksSchema.shape[eventKey]
      return eventSchema?.shape?.url
    })(),
    getDynamicProps: (values: AgentSettingsFormValues) => ({
      inputProps: {
        value: values.event_callbacks?.[fieldPath?.split('.')?.[1] as EventCallbackKey]?.url,
      },
    }),
  },
  {
    name: 'auth-config',
    type: 'basic',
    component: ({ form: fieldForm }) => (
      <AuthConfig form={fieldForm} fieldPath={fieldPath} effectiveShowAuth={effectiveShowAuth} />
    ),
  },
  {
    name: `${fieldPath}.transcription`,
    type: 'sub-item',
    componentType: 'checkbox',
    onChangeListenTo: ['event_callbacks.RECORDING.url'],
    visible: fieldPath?.split('.')?.[1] === 'RECORDING',
    // Currently using a single 'transcription' key on the backend that handles both transcription and summary.
    // Future enhancement: If separate controls are needed, add a new 'summary' key to the node schema
    // in 'phlo_config_service/config_service/functions.py' -> Update schema
    // in 'plivocx-service/runner-service/usecases/phloexecution/asyncevents.go' -> Update handler
    inputProps: {
      label: 'Include Transcription & Summary',
      'data-testid': 'checkbox-transcription',
    },
    zodValidation: (() => {
      const eventKey = fieldPath?.split('.')?.[1] as EventCallbackKey
      if (eventKey === 'RECORDING') {
        return eventCallbacksSchema.shape.RECORDING.shape.transcription
      }
      return undefined
    })(),
    disabled: (values) =>
      fieldPath?.split('.')?.[1] === 'RECORDING' && !values.event_callbacks?.RECORDING?.url?.trim(),
  },
]

export const EventCallbackField = React.memo(
  ({ label, form, fieldPath }: EventCallbackFieldProps) => {
    const { id } = useParams()
    const queryClient = useQueryClient()
    const flowData = queryClient.getQueryData(flowQueryKeys().flowData(id || '')) as FlowData
    const [isOpen, onOpenChange] = useState(false)
    const currentAuth =
      form.state.values.event_callbacks?.[fieldPath?.split('.')?.[1] as EventCallbackKey]?.auth

    const [showAuthSection, setShowAuthSection] = useState(
      currentAuth !== undefined && currentAuth !== AuthType.NO_AUTH,
    )

    const handleToggleAuth = useMemo(
      () => () => {
        const currentValue = getCurrentValue(fieldPath, form.state.values)

        if (showAuthSection) {
          updateFieldValue(form, fieldPath, {
            ...currentValue,
            auth: AuthType.NO_AUTH,
            username: '',
            password: '',
            token: '',
          })
          setShowAuthSection(false)
        } else {
          updateFieldValue(form, fieldPath, {
            ...currentValue,
            auth: AuthType.BASIC_AUTH,
          })
          setShowAuthSection(true)
        }
      },
      [fieldPath, form, showAuthSection],
    )

    const effectiveShowAuth =
      showAuthSection && getCurrentValue(fieldPath, form.state.values)?.auth !== AuthType.NO_AUTH

    const allNodeVars = useAllNodeVars()

    const generateSchema = generateEventCallbackSchema({
      fieldPath,
      flow: {
        name: flowData?.phlo?.name,
        id: flowData?.phlo?.id,
      },
      allNodeVars,
      transcription: form.state.values.event_callbacks?.RECORDING?.transcription,
    })

    const fields = useMemo(
      () =>
        createEventCallbackFields(
          fieldPath,
          label,
          effectiveShowAuth,
          isOpen,
          onOpenChange,
          generateSchema,
          handleToggleAuth,
          form,
        ),
      [
        fieldPath,
        label,
        effectiveShowAuth,
        isOpen,
        onOpenChange,
        generateSchema,
        handleToggleAuth,
        form,
      ],
    )

    return (
      <section className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
        <FieldsRenderer form={form} fields={fields} />
      </section>
    )
  },
)

export default EventCallbackField
